import React, { useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IResolveParams } from 'reactjs-social-login';

import { useAppDispatch } from '../../app/hook';
import { useSocialAuthLoginMutation } from '../../app/services/auth-rtk.service';
import { AlertsType } from '../../constants/alerts.constant';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import { login as authSliceLogin, setError } from '../../features/auth/auth.slice';

type SocialAuthParamsType = {
  isSocialAuthLoading: boolean;
  handleLoginStartSocialAuth: () => void;
  handleResolveSocialAuth: (provider: IResolveParams['provider'], data: IResolveParams['data']) => void;
  handleRejectSocialAuth: (err: any) => void;
};

export function withSocialAuth<P extends SocialAuthParamsType = SocialAuthParamsType>(WrappedComponent: React.ComponentType<P>) {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithSocialAuth = (props: P) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [cookies] = useCookies(['utm_datas', '_fbc', '_fbp']);

    const [socialAuthLogin, { isLoading: isSocialAuthLoading }] = useSocialAuthLoginMutation();

    useEffect(() => {
      dispatch(setError(null));
    }, []);

    const handleLoginStartSocialAuth = () => undefined;

    const serviceError: string | null = null;

    const handleResolveSocialAuth = (provider: IResolveParams['provider'], data: IResolveParams['data']) => {
      const cpPayload = { ...data, provider };

      dispatch(setError(null));

      const newData: any = { ...{ type: 'login', providerPayload: cpPayload } };

      if (window.navigator.cookieEnabled && cookies.utm_datas && cookies.utm_datas !== '') {
        newData.user_utm_data = cookies.utm_datas;

        newData.user_utm_data = {
          ...newData.user_utm_data,
          client_user_agent: navigator.userAgent,
        };

        if (cookies.utm_datas.fbclid) {
          if (cookies._fbp && cookies._fbp !== '') {
            newData.user_utm_data = {
              ...newData.user_utm_data,
              fbp: cookies._fbp,
            };
          }

          if (cookies._fbc && cookies._fbc !== '') {
            newData.user_utm_data = {
              ...newData.user_utm_data,
              fbc: cookies._fbc,
            };
          }
        }
      }

      socialAuthLogin(newData)
        .unwrap()
        .then(res => {
          localStorage.setItem(`LOUISEBOT_token`, res?.token);
          localStorage.setItem(`LOUISEBOT_refresh_token`, res?.refresh_token);
          localStorage.setItem(`LOUISEBOT_user_access_token`, res?.userAccessToken);

          dispatch(
            authSliceLogin({
              is_remember_me: true,
            })
          );
          if (res.statusCode && res.statusCode === 201)
            navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`, {
              replace: true,
            });
          else
            navigate(`/${MultiLanguageUrl[i18n.language] || 'en-US'}/control-panel`, {
              replace: true,
            });
        })
        .catch(err => {
          dispatch(setError(err?.data?.message));
        });
    };

    const handleRejectSocialAuth = (err: any) => {
      if (err.error && err.error !== 'popup_closed_by_user') toast.error(t(AlertsType.ISATKO017));
    };

    const withSocialAuthProps = {
      isSocialAuthLoading: isSocialAuthLoading,
      onLoginStartSocialAuthStart: handleLoginStartSocialAuth,
      onResolveSocialAuth: handleResolveSocialAuth,
      onRejectSocialAuth: handleRejectSocialAuth,
      serviceError,
    };

    return <WrappedComponent {...withSocialAuthProps} {...(props as P)} />;
  };

  ComponentWithSocialAuth.displayName = `withSocialAuth(${displayName}`;

  return ComponentWithSocialAuth;
}
